<script lang="ts">

import { Component, Prop, Vue } from "vue-facing-decorator";
import LogoComponent from "@/components/main-header/Logo.vue";
import MenuComponent from "@/components/main-header/Menu.vue";
import { SearchResultItem, } from "@/domain/main-header/entity/SearchResultItem.interface";
import {
    AnalyticsCustomEvent,
    GTMUtils,
    IconName,
    LanguageCode,
    MainHeaderQASelectors,
    TranslationKey
} from "webshop-modules";
import QuickSearch from "@/components/quick-search/QuickSearch.vue";
import QuickSearchResults from "@/components/quick-search/Results.vue";
import IconComponent from "@/components/atoms/Icon.vue";
import { MenuTitle } from "@/domain/main-header/entity/MenuTitle.interface";
import { CartStatus } from "@/domain/main-header/entity/CartStatus.interface";
import { UserStatus } from "@/domain/main-header/entity/UserStatus.interface";
import { Profile } from "@/domain/main-header/entity/Profile.interface";
import { IMainHeader } from "@/domain/main-header/entity/MainHeader.interface";
import { ProfileCategory } from "@/domain/main-header/entity/ProfileCategory.interface";
import { MainHeaderTitle } from "@/domain/main-header/entity/MainHeaderTitle.enum";
import { LanguageConfiguration } from "@/domain/main-header/entity/LanguageConfiguration.interface";
import { DOMElementEvent } from "webshop-modules/dom/DOMElementEvent.enum";

@Component({
    components: {
        IconComponent,
        LogoComponent,
        MenuComponent,
        QuickSearch,
        QuickSearchResults
    },
})
export default class MainHeader extends Vue {

    @Prop() readonly data!: IMainHeader;

    public menuActive = false;
    public searchActive = false;
    public activeSection: MenuTitle | Profile | null = null;
    public cartStatus: CartStatus | null = null;
    public userStatus: UserStatus | null = null;
    public searchString = '';
    public searchResults: SearchResultItem[] | null = null;
    public promotionsData: any | null = null;
    public cartUrl: string | null = null;
    public profileOnly = false;
    public isLanguagedropdownVisible = false;
    public searchTranslation = '';

    public readonly IconName: typeof IconName = IconName;
    public readonly MainHeaderTitle: typeof MainHeaderTitle = MainHeaderTitle;
    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly MainHeaderQASelectors: typeof MainHeaderQASelectors = MainHeaderQASelectors;

    public get profileConfiguration(): Profile {
        return this.data.profile;
    }

    public getSelectableLanguageConfiguration(): LanguageConfiguration {
        const url = window.location.href;
        const langConfiguration = this.data.langSwitcher;

        if (url.includes(`/${LanguageCode.English}/`)) {
            return <LanguageConfiguration>langConfiguration.find(language => language.lang !== LanguageCode.English);
        }

        return <LanguageConfiguration>langConfiguration.find(language => language.lang === LanguageCode.English);
    }

    public getNonSelectableLanguageConfiguration(): LanguageConfiguration {
        const url = window.location.href;
        const langConfiguration = this.data.langSwitcher;

        if (url.includes(`/${LanguageCode.English}/`)) {
            return <LanguageConfiguration>langConfiguration.find(language => language.lang === LanguageCode.English);
        }

        return <LanguageConfiguration>langConfiguration.find(language => language.lang !== LanguageCode.English);
    }

    public shouldDisplayCart(): boolean {
        return !!(this.cartStatus && this.data.profile && this.cartUrl !== null);
    }

    public updateSearchQuery(query: string): void {
        this.searchResults = null;
        const quickSearchRef: any = this.$refs.quickSearch as QuickSearch;
        quickSearchRef.updateSearchString(query);
    }

    public toggleLanguageDropdown(event: Event): void {
        event.stopPropagation();
        this.isLanguagedropdownVisible = !this.isLanguagedropdownVisible;
    }

    public handleClickOutsideDropdown(event: Event): void {
        const dropdown = this.$refs.languageDropdown as HTMLElement;

        if (dropdown && !dropdown.contains(<Node>event.target) && this.isLanguagedropdownVisible) {
            this.isLanguagedropdownVisible = false;
        }
    }

    public isActiveSection(section: MainHeaderTitle): boolean {
        if (this.activeSection && this.activeSection.sectionName === section) {
            return true;
        }

        return false;
    }

    public handleSearch(): void {
        const eventData: AnalyticsCustomEvent = {
            event: 'main_navigation_click',
            click_text: 'search'
        };

        GTMUtils.pushAnalyticsEventToDataLayer(eventData);
    }

    public shouldDisplayProfile(): boolean {
        return !!this.data.profile && !this.profileOnly;
    }

    public menuBack(): void {
        this.activeSection = null;
    }

    public toggleContent(section: MainHeaderTitle, event: Event): void {
        let sectionData;

        if (section === MainHeaderTitle.PROFILE) {
            sectionData = this.data.profile;
        } else {
            sectionData = this.data.menu[section];
        }

        if (!sectionData) {
            return;
        }

        if (sectionData.categories && sectionData.categories.length) {
            event.preventDefault();

            if (!this.menuActive) {
                this.toggleMenu();
            }

            if (this.activeSection?.sectionName !== sectionData.sectionName) {
                this.activeSection = sectionData;
            } else {
                this.toggleMenu();
            }
        }

        const eventData: AnalyticsCustomEvent = {
            event: 'main_navigation_click',
            click_text: section,
        };

        GTMUtils.pushAnalyticsEventToDataLayer(eventData);
    }

    public isQuickSearchEnabled(): boolean {
        return !!this.data.quickSearch && !this.activeSection && !this.menuActive || this.searchActive
    }

    public actionMenu(section: MainHeaderTitle, event: Event): void {
        if (this.profileOnly && !this.searchActive) {
            this.toggleContent(section, event);
        } else {
            this.toggleMenu();
        }
    }

    public toggleSearch() {
        if (!this.searchActive) {
            this.toggleMenu();
            this.searchActive = this.menuActive;
        }

        const eventData: AnalyticsCustomEvent = {
            event: 'main_navigation_click',
            click_text: 'search'
        };

        GTMUtils.pushAnalyticsEventToDataLayer(eventData);
    }

    public updateSearch(query: string, results: SearchResultItem[]): void {
        if (results) {
            this.searchString = query;
            this.searchResults = results;
        }
    }

    public toggleLanguage(isDisabled: boolean) {
        if (isDisabled) {
            return;
        }

        this.isLanguagedropdownVisible = false;
        const url = new URL(window.location.href);
        url.searchParams.set('switch_language', '');

        window.location.href = url.toString();
    }

    private created() {
        if (this.data.profile) {
            this.setAuthAndCartStatus();
        }
    }

    private isProfileOnly(): boolean {
        if (!this.data.menu) {
            return true;
        }

        return false;
    }

    private getCartUrl(): string | null {
        if (!this.data.profile) {
            return null;
        }

        const cartCategory = this.data.profile.categories.find((e: ProfileCategory) => e.showCartStatus);

        return cartCategory ? cartCategory.url : null;
    }

    private setAuthAndCartStatus() {
        // set cookie name
        const userStatusCacheCookie = {'name': 'X-Auth', 'value': null};
        const cartStatusCacheCookie = {'name': 'cart_state', 'value': null};

        // read cookie
        const userStatusCacheCookieRes = new RegExp('(?:^|; )' + encodeURIComponent(userStatusCacheCookie.name) + '=([^;]*)')
            .exec(document.cookie);
        const cartStatusCacheCookieRes = new RegExp('(?:^|; )' + encodeURIComponent(cartStatusCacheCookie.name) + '=([^;]*)')
            .exec(document.cookie);

        // check if cookie is set
        if (userStatusCacheCookieRes) {
            // decode and parse cookie values
            userStatusCacheCookie.value = JSON.parse(atob(decodeURIComponent(userStatusCacheCookieRes[1])));
            this.userStatus = userStatusCacheCookie.value;
        }
        if (cartStatusCacheCookieRes) {
            // decode and parse cookie values
            cartStatusCacheCookie.value = JSON.parse(atob(decodeURIComponent(cartStatusCacheCookieRes[1])));
            this.cartStatus = cartStatusCacheCookie.value;
        }
    }

    private toggleMenu() {
        const htmlElement = document.querySelector('html');
        const overlayElement = document.querySelector('[data-js=mainmenu-overlay]');

        if (this.menuActive) {
            if (htmlElement) {
                htmlElement.classList.remove('mainMenu-opened');
            }
            if (overlayElement) {
                overlayElement.classList.remove('is-opened');
            }
            this.activeSection = null;
        } else {
            if (htmlElement) {
                htmlElement.classList.add('mainMenu-opened');
            }
            if (overlayElement) {
                overlayElement.classList.add('is-opened');
            }
        }

        this.menuActive = !this.menuActive;
        this.searchActive = false;
    }

    private async mounted() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const instance = this;
        const menuOverlay = document.querySelector('[data-js=mainmenu-overlay]');

        this.cartUrl = this.getCartUrl();
        this.profileOnly = this.isProfileOnly();
        this.searchTranslation = await this.$translationService.getTranslationFor(TranslationKey.Search)

        if (menuOverlay) {
            menuOverlay.addEventListener('click', function () {
                if (instance.menuActive) {
                    instance.toggleMenu();
                }
            });
        }

        document.addEventListener(DOMElementEvent.Click, this.handleClickOutsideDropdown);
    }

    private beforeDestroy() {
        document.removeEventListener(DOMElementEvent.Click, this.handleClickOutsideDropdown);
    }
}
</script>

<template>
    <header class="o-mainHeader" role="banner" data-js="seatmap-controls">
        <div class="l-container is-fullwidth">
            <div class="o-mainHeader__inner">
                <template v-if="data.logo">
                    <meta itemprop="name" :content="data.logo.name"/>
                    <LogoComponent v-if="data.logo" :logo="data.logo"/>
                </template>
                <div v-else class="o-mainHeader__blankLogoPlaceholder"></div>

                <div v-if="menuActive && !searchActive" class="o-mainHeader__utilities">
                    <div v-if="activeSection && !profileOnly" class="o-mainHeader__menuBack" @click="menuBack()">
                        <span class="a-iconArr icon-larr"><IconComponent :icon-id="IconName.ArrowSlim"/></span>
                        <span class="o-mainHeader__menuBackTitle">{{ activeSection.title }}</span>
                    </div>
                    <nav v-else class="o-mainHeader__utilitiesNav">
                        <span v-if="shouldDisplayProfile()"
                              class="o-mainHeader__utilitiesNavItem"
                              @click="toggleContent(MainHeaderTitle.PROFILE, $event)"
                              :data-qa="MainHeaderQASelectors.ICON_PROFILE">
                            <IconComponent :icon-id="IconName.Profile"/>
                        </span>

                        <a v-if="shouldDisplayCart()"
                           class="o-mainHeader__utilitiesNavItem"
                           :href="cartUrl">
                            <IconComponent :icon-id="IconName.Cart"/>
                            <span class="o-mainHeader__utilitiesNavItemDot"></span>
                        </a>

                        <div class="o-mainHeader__dropdownWrapper" @click="toggleLanguageDropdown($event)">
                            <a v-if="getNonSelectableLanguageConfiguration()"
                               class="o-mainHeader__utilitiesNavItem -less-margin">
                                <IconComponent :icon-id="getNonSelectableLanguageConfiguration().icon"/>
                            </a>
                            <strong>{{ getNonSelectableLanguageConfiguration().lang.toUpperCase() }}</strong>
                        </div>
                        <transition name="dropdown">
                            <div v-if="isLanguagedropdownVisible"
                                 class="o-mainHeader__dropdown"
                                 ref="languageDropdown">
                                <a v-for="config in data.langSwitcher"
                                   :class="{'-disabled': config.lang !== getSelectableLanguageConfiguration().lang}"
                                   class="o-mainHeader__dropdownOption"
                                   @click="toggleLanguage(config.lang !== getSelectableLanguageConfiguration().lang)">
                                    <span :class="{
                                        'o-mainHeader__circle--filled': config.lang !== getSelectableLanguageConfiguration().lang,
                                        'o-mainHeader__circle--empty': config.lang === getSelectableLanguageConfiguration().lang
                                         }"
                                         class="o-mainHeader__circle"></span>
                                    <strong>{{ `${config.lang.toUpperCase()} - ${config.name}` }}</strong>
                                </a>
                            </div>
                        </transition>
                    </nav>
                </div>

                <QuickSearch v-if="isQuickSearchEnabled()"
                             :isActive="searchActive"
                             :searchParam="data.quickSearch.searchString"
                             :searchUrl="data.quickSearch.searchPath"
                             @toggleSearch="toggleSearch"
                             @updateSearch="updateSearch"
                             ref="quickSearch"
                ></QuickSearch>

                <nav v-if="data.menu" class="o-mainHeader__nav">
                    <ul class="o-mainHeader__mainMenu">

                        <li v-for="(item, idx) in data.menu"
                            :key="idx"
                            :class="['o-mainHeader__mainMenuItem']"
                        >
                            <a :href="item.url"
                               :aria-label="item.title"
                               @click="toggleContent(item.sectionName, $event)"
                               :class="[
                                   'o-mainHeader__mainMenuItemLink',
                                   {'is-selected': activeSection && item.name === activeSection.name}
                               ]"
                            >{{ item.title }}</a>
                        </li>
                    </ul>
                </nav>

                <div v-if="data.profile || data.quickSearch" class="o-mainHeader__utilitiesDesktop">
                    <a v-if="data.quickSearch" class="o-mainHeader__utilitiesDesktop-action"
                       :href="data.quickSearch.searchPath"
                       :aria-label="searchTranslation"
                       v-on:click="handleSearch">
                        <IconComponent :icon-id="IconName.Search"/>
                    </a>

                    <a v-if="data.profile"
                       class="o-mainHeader__utilitiesDesktop-action"
                       :href="profileConfiguration.url"
                       :aria-label="profileConfiguration.name"
                       :data-qa="MainHeaderQASelectors.ICON_PROFILE"
                       @click="toggleContent(MainHeaderTitle.PROFILE, $event)">
                        <IconComponent :icon-id="IconName.Profile"/>
                        <span v-if="shouldDisplayCart()" class="o-mainHeader__utilitiesNavItemDot"></span>
                    </a>
                </div>

                <div v-if="data.menu || data.profile || data.langSwitcher"
                     :class="['o-mainHeader__triggerMenu', {'is-opened': menuActive}]"
                     @click="actionMenu(MainHeaderTitle.PROFILE, $event)"
                >
                    <IconComponent v-if="searchActive" :icon-id="IconName.ArrowSlim"/>
                    <div v-else class="o-mainHeader__triggerMenuIcon"></div>
                </div>
            </div>
        </div>

        <div :class="{
                'o-mainHeader__drawer': true,
                'is-opened': menuActive,
                'is-narrow': isActiveSection(MainHeaderTitle.PROFILE)
             }"
        >
            <div class="l-container is-fullwidth">

                <QuickSearchResults v-if="data.quickSearch && searchActive"
                                    :query="searchString"
                                    :results="searchResults"
                                    :searchUrl="data.quickSearch.searchPath"
                                    :searchParam="data.quickSearch.searchString"
                                    @updateSearch="updateSearchQuery"
                ></QuickSearchResults>

                <MenuComponent v-else
                               :cartStatus="cartStatus"
                               :userStatus="userStatus"
                               :data="data"
                               :activeSection="activeSection"
                               :languageConfiguration="getSelectableLanguageConfiguration()"
                               @toggleContent="toggleContent"
                ></MenuComponent>
            </div>
        </div>
    </header>
</template>

<style scoped>
    @keyframes slide-in {
        from {
            transform: translateY(-30px);
            opacity: 0;
        }
        to {
            transform: translateY(-15px);
            opacity: 1;
        }
    }

    @keyframes slide-out {
        from {
            transform: translateY(-15px);
            opacity: 1;
        }
        to {
            transform: translateY(-30px);
            opacity: 0;
        }
    }

    .dropdown-enter-active {
        animation: slide-in 0.3s forwards;
    }

    .dropdown-leave-active {
        animation: slide-out 0.3s forwards;
    }
</style>